import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Alert, Input } from 'reactstrap'
import moment from 'moment-timezone'
import { useQuery } from '@apollo/client'
import { GetConfigurationSettings } from '../../../Functions/querys'

const ScheduleModal = ({ isOpen, toggle, onSchedule, driverId, userPrefferedTz }) => {
  const fmtDatetimeLocal = (dt) => dt.format(moment.HTML5_FMT.DATETIME_LOCAL)


  const [error, setError] = useState('')
  const [driverBranchTz, setDriverBranchTz] = useState(null)

  useQuery(GetConfigurationSettings, {
    skip: !driverId,
    variables: { driverId, name: "PREFERRED_TIMEZONE" },
    fetchPolicy: "cache-first",
    onCompleted: (data) => {
      setDriverBranchTz(data?.getConfigurationSettings || null)
    },
  })

  /** Determine timezone (memoized) */
  const timezone = useMemo(
    () => driverBranchTz || userPrefferedTz || moment.tz.guess(),
    [driverBranchTz, userPrefferedTz]
  )

  useEffect(() => {
    setDateTime(fmtDatetimeLocal(moment().tz(timezone)))
  }, [timezone])

  /** Current time in that timezone (memoized for stability) */
  const nowTz = useMemo(() => moment().tz(timezone), [timezone])

  const [dateTime, setDateTime] = useState(fmtDatetimeLocal(nowTz))

  /** Quick options */
  const quickOptions = useMemo(
    () => [
      { label: 'In 1 hour', value: nowTz.clone().add(1, 'hour') },
      { label: 'In 2 hours', value: nowTz.clone().add(2, 'hours') },
      { label: 'Tomorrow 9 AM', value: nowTz.clone().add(1, 'day').hour(9).minute(0) },
      { label: 'Next Monday 9 AM', value: nowTz.clone().day(8).hour(9).minute(0) },
    ],
    [nowTz]
  )

  /** Validation and schedule handler */
  const handleSchedule = useCallback(() => {
    if (!dateTime) {
      setError('Please select both date and time')
      return
    }

    const scheduled = moment.tz(dateTime, moment.HTML5_FMT.DATETIME_LOCAL, timezone)

    if (scheduled.isBefore(nowTz)) {
      setError('Scheduled time must be in the future')
      return
    }

    onSchedule(scheduled)
    toggle()
  }, [dateTime, onSchedule, toggle, timezone, nowTz])

  /** Human-readable output */
  const formattedDisplay = useMemo(() => {
    if (!dateTime) return null
    return `${moment.tz(dateTime, moment.HTML5_FMT.DATETIME_LOCAL, timezone)
      .format('dddd, MMMM D, YYYY [at] h:mm A')} (${moment.tz(timezone).format("z")})`
  }, [dateTime, timezone])


  return (
    <Modal isOpen={isOpen} centered>
      <ModalHeader>Schedule Message</ModalHeader>

      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}

        <div className="mb-3">
          <Label className="text-muted fw-bold" style={{ fontSize: '12px' }}>Quick Options</Label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {quickOptions.map(({ label, value }, i) => (
              <Button
                key={i}
                size="sm"
                outline
                color="secondary"
                style={{ fontSize: '12px' }}
                onClick={() => {
                  setDateTime(fmtDatetimeLocal(value))
                  setError('')
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        <FormGroup>
          <Label for="schedule-date">Date</Label>
          <Input
            type="datetime-local"
            id="schedule-date"
            value={dateTime}
            min={fmtDatetimeLocal(nowTz)}
            max={fmtDatetimeLocal(nowTz.clone().add(5, 'day'))}
            onChange={(e) => setDateTime(e.target.value)}
          />
        </FormGroup>

        {dateTime && (
          <div className="mt-3 p-2 bg-light rounded">
            <div className="text-muted small mb-1">Scheduled for:</div>
            <div className="fw-bold">{formattedDisplay}</div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>Cancel</Button>
        <Button color="primary" onClick={handleSchedule}>Schedule</Button>
      </ModalFooter>
    </Modal>
  )
}

export default ScheduleModal
